/* SharePreview (Phase 118): shows admins how a PUBLIC report card will look
   on Facebook and X before they share it. Built from the exact Open Graph /
   Twitter values the share link publishes (admin endpoint), so what is shown
   here is what crawlers read. Platforms fetch and cache their own copy, so
   the note below is honest about small differences. Private reports show a
   clear "not shareable" state instead of a card. */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, Facebook, Loader2, RefreshCw, Share2, ShieldOff, Twitter } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { api } from '../lib/api';

const FacebookCard = ({ m }) => (
  <div className="max-w-[500px] rounded-lg border border-[#ced0d4] bg-white overflow-hidden shadow-sm" data-testid="share-preview-facebook-card">
    <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
      <img src={m.image} alt="Social preview" className="w-full h-full object-cover" data-testid="share-preview-facebook-image" />
    </div>
    <div className="bg-[#f0f2f5] px-3 py-2.5 border-t border-[#ced0d4]">
      <p className="text-[12px] uppercase text-[#65676b] truncate" data-testid="share-preview-facebook-domain">{m.domain}</p>
      <p className="text-[16px] font-semibold text-[#050505] leading-5 line-clamp-2" data-testid="share-preview-facebook-title">{m.title}</p>
      <p className="text-[14px] text-[#65676b] leading-5 line-clamp-1" data-testid="share-preview-facebook-description">{m.description}</p>
    </div>
  </div>
);

const XCard = ({ m }) => (
  <div className="max-w-[500px] rounded-2xl border border-[#cfd9de] bg-white overflow-hidden relative" data-testid="share-preview-x-card">
    <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
      <img src={m.image} alt="Social preview" className="w-full h-full object-cover" data-testid="share-preview-x-image" />
    </div>
    <span className="absolute left-3 bottom-3 rounded bg-black/70 text-white text-[13px] px-1.5 py-0.5 max-w-[90%] truncate" data-testid="share-preview-x-title">{m.title}</span>
    <p className="text-[13px] text-[#536471] px-1 pt-1.5 pb-0.5" data-testid="share-preview-x-domain">From {m.domain}</p>
  </div>
);

export const SharePreview = ({ issueId, testId = 'share-preview' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [platform, setPlatform] = useState('facebook');
  const [bust, setBust] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await api.get(`/admin/issues/${issueId}/share-preview`); setData(r.data); } catch (e) { setError(e.response?.data?.detail || 'Could not load the share preview'); } finally { setLoading(false); }
  }, [issueId]);
  useEffect(() => { load(); }, [load, bust]);

  const refresh = () => setBust((b) => b + 1);

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><Share2 className="h-4 w-4" /> Share preview (how the card looks before you share)</p>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading} className="h-9 text-xs font-bold btn-press" data-testid={`${testId}-refresh`}>{loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Refresh</Button>
      </div>
      {loading && !data && <p className="text-xs text-slate-500">Building the preview from the public share link...</p>}
      {error && <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`${testId}-error`}>{error}</p>}
      {data && !data.shareable && (
        <div className="flex items-start gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2.5" data-testid={`${testId}-private`}>
          <ShieldOff className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
          <span><strong>Not shareable.</strong> {data.reason}</span>
        </div>
      )}
      {data && data.shareable && (
        <>
          <Tabs value={platform} onValueChange={setPlatform}>
            <TabsList className="h-9">
              <TabsTrigger value="facebook" className="text-xs" data-testid={`${testId}-tab-facebook`}><Facebook className="h-3.5 w-3.5 mr-1" /> Facebook</TabsTrigger>
              <TabsTrigger value="x" className="text-xs" data-testid={`${testId}-tab-x`}><Twitter className="h-3.5 w-3.5 mr-1" /> X</TabsTrigger>
            </TabsList>
          </Tabs>
          {platform === 'facebook' ? <FacebookCard m={{ ...data, image: `${data.image}${data.image.includes('?') ? '&' : '?'}v=${bust}` }} /> : <XCard m={{ ...data, image: `${data.image}${data.image.includes('?') ? '&' : '?'}v=${bust}` }} />}
          {data.warnings?.length > 0 && (
            <ul className="space-y-1" data-testid={`${testId}-warnings`}>
              {data.warnings.map((w) => (
                <li key={w} className="flex items-start gap-1.5 text-xs text-amber-900"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}</li>
              ))}
            </ul>
          )}
          <details className="text-xs" data-testid={`${testId}-tags`}>
            <summary className="cursor-pointer font-bold text-[#1a2744]">What crawlers read from the share link</summary>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 bg-white border border-slate-200 rounded-lg p-2.5 font-mono text-[11px] text-slate-700 break-all">
              <dt className="text-slate-500">og:title</dt><dd data-testid={`${testId}-tag-title`}>{data.title}</dd>
              <dt className="text-slate-500">og:description</dt><dd data-testid={`${testId}-tag-description`}>{data.description}</dd>
              <dt className="text-slate-500">og:image</dt><dd data-testid={`${testId}-tag-image`}>{data.image} ({data.image_kind === 'collage' ? `collage of ${data.public_photo_count} public photos` : data.image_kind === 'photo' ? 'the single public photo' : 'branded card, no public photos'}; {data.image_size})</dd>
              <dt className="text-slate-500">og:url</dt><dd data-testid={`${testId}-tag-url`}>{data.url}</dd>
              <dt className="text-slate-500">og:site_name</dt><dd>{data.site_name}</dd>
              <dt className="text-slate-500">twitter:card</dt><dd>{data.twitter_card}</dd>
              <dt className="text-slate-500">share link</dt><dd data-testid={`${testId}-tag-share-url`}>{data.share_url}</dd>
            </dl>
          </details>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <a href={data.debugger_links?.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2 min-h-9" data-testid={`${testId}-facebook-debugger`}><ExternalLink className="h-3.5 w-3.5" /> Facebook Sharing Debugger (refresh Facebook's cached copy)</a>
            <a href={data.share_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2 min-h-9" data-testid={`${testId}-open-share-link`}><ExternalLink className="h-3.5 w-3.5" /> Open the share link</a>
          </div>
          <p className="text-[11px] text-slate-500">{data.note} X no longer offers a public card validator; the X mock-up follows its current large-image card layout.</p>
        </>
      )}
    </div>
  );
};
